import { Link } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { api } from '../../services/api';
import type { FooterContent, ContactContent } from '../../types/content';
import { logger } from '../../utils/logger';
import Icon from '../common/Icon';
import { analytics } from '../../services/analytics';

const Footer = () => {
	const [footerContent, setFooterContent] = useState<FooterContent | null>(null);
	const [contactContent, setContactContent] = useState<ContactContent | null>(null);

	useEffect(() => {
		const fetchFooterContent = async () => {
			try {
				const response = await api.getFooter();
				setFooterContent(response.data as FooterContent);
			} catch (err) {
				logger.error('Error fetching footer content:', err);
				// Fallback to default values
				const currentYear = new Date().getFullYear();
				setFooterContent({
					copyright: `© ${currentYear} S NEWHAM Groundworks | All Rights Reserved`,
				});
			}
		};

		const fetchContactContent = async () => {
			try {
				const response = await api.getContact();
				setContactContent(response.data as ContactContent);
			} catch (err) {
				logger.error('Error fetching contact content for footer:', err);
			}
		};

		fetchFooterContent();
		fetchContactContent();
	}, []);

	// Transform Contact socialLinks object to array format for rendering
	const socialLinks = useMemo(() => {
		if (!contactContent?.socialLinks) return [];
		
		const links: Array<{ platform: string; url: string }> = [];
		if (contactContent.socialLinks.instagram) {
			links.push({ platform: 'Instagram', url: contactContent.socialLinks.instagram });
		}
		if (contactContent.socialLinks.facebook) {
			links.push({ platform: 'Facebook', url: contactContent.socialLinks.facebook });
		}
		if (contactContent.socialLinks.twitter) {
			links.push({ platform: 'Twitter', url: contactContent.socialLinks.twitter });
		}
		return links;
	}, [contactContent?.socialLinks]);

	const footerStyle = footerContent?.backgroundImage
		? {
				backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("${footerContent.backgroundImage}")`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				backgroundRepeat: 'no-repeat',
			}
		: undefined;

	return (
		<footer role="contentinfo" style={footerStyle}>
			<div className="footer-container">
				<div className="footer-section">
					<h3>S NEWHAM Groundworks</h3>
					<p>Professional groundwork company in Ringwood</p>
					{socialLinks.length > 0 && (
						<div className="footer-social-links">
							{socialLinks.map((link, index) => {
								const platformLower = link.platform.toLowerCase();
								let iconName = 'link';
								if (platformLower.includes('instagram')) iconName = 'instagram';
								else if (platformLower.includes('facebook')) iconName = 'facebook';
								else if (platformLower.includes('twitter')) iconName = 'twitter';
								else if (platformLower.includes('linkedin')) iconName = 'linkedin';
								else if (platformLower.includes('youtube')) iconName = 'youtube';

								return (
									<a
										key={index}
										href={link.url}
										target="_blank"
										rel="noopener noreferrer"
										aria-label={`Visit our ${link.platform} page`}
										className="social-link"
										onClick={() => analytics.trackExternalLink(link.url, link.platform)}
									>
										<Icon name={iconName} size={24} className="social-icon" />
									</a>
								);
							})}
						</div>
					)}
				</div>
				<div className="footer-section">
					<h4>Quick Links</h4>
					<nav aria-label="Footer navigation">
						<ul>
							<li>
								<Link to="/">Home</Link>
							</li>
							<li>
								<Link to="/services">Services</Link>
							</li>
							<li>
								<Link to="/gallery">Gallery</Link>
							</li>
							<li>
								<Link to="/testimonials">Testimonials</Link>
							</li>
							<li>
								<Link to="/contact">Contact</Link>
							</li>
							<li>
								<Link to="/faq">FAQ</Link>
							</li>
							<li>
								<Link to="/areas-covered">Areas Covered</Link>
							</li>
						</ul>
					</nav>
				</div>
				<div className="footer-section">
					<h4>Contact</h4>
					<p>
						<Icon name="phone" size={16} className="footer-contact-icon" />
						<a
							href="tel:07824612098"
							onClick={() => analytics.trackPhoneClick('07824 612 098')}
						>
							07824 612 098
						</a>
					</p>
					<p>
						<Icon name="mail" size={16} className="footer-contact-icon" />
						<a
							href="mailto:info@snewhamgroundworks.co.uk"
							onClick={() => analytics.trackEmailClick('info@snewhamgroundworks.co.uk')}
						>
							info@snewhamgroundworks.co.uk
						</a>
					</p>
					<p>
						<Icon name="map-pin" size={16} className="footer-contact-icon" />
						Based in Ringwood
					</p>
				</div>
			</div>
			<div className="footer-bottom">
				<p>
					{footerContent?.copyright ||
						`© ${new Date().getFullYear()} S NEWHAM Groundworks | All Rights Reserved`}
				</p>
				<div className="footer-external-links">
					<a
						href="https://www.checkatrade.com/trades/snewhamgroundworks"
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Visit our Checkatrade profile"
						className="footer-external-link checkatrade-link"
						onClick={() => analytics.trackExternalLink('https://www.checkatrade.com/trades/snewhamgroundworks', 'Checkatrade')}
					>
						<img
							src="/checkatrade.png"
							alt="Checkatrade - Where reputation matters"
							className="checkatrade-logo"
						/>
					</a>
					<a
						href="https://g.page/r/CZO6bJamyobpEAI/review"
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Leave us a review on Google"
						className="footer-external-link google-review-link"
						onClick={() => analytics.trackExternalLink('https://g.page/r/CZO6bJamyobpEAI/review', 'Google Review')}
					>
						<img
							src="https://www.gstatic.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
							alt="Google"
							className="google-logo"
						/>
						<span>Leave us a review</span>
					</a>
				</div>
			</div>
		</footer>
	);
};

export default Footer;
